import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createAxiosPayment, payoutsApiRequest } from "../utils/request";
import { payoutsApis } from "../utils/apis";
import { getUsers } from "../utils/getUser";

const user = getUsers();

export const listAllPayouts = createAsyncThunk(
  "listAllPayouts",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const params = new URLSearchParams();

      // Add basic params
      params.append("skip", payload.skip);
      params.append("limit", payload.limit);

      // Add each status separately
      if (Array.isArray(payload.payout_status)) {
        payload.payout_status.forEach((status) => {
          params.append("payout_status", status);
        });
      }

      // Add each type separately
      if (Array.isArray(payload.type)) {
        payload.type.forEach((type) => {
          params.append("type", type);
        });
      }
      if (payload?.search) {
        params.append("search", payload?.search);
      }
      if (payload?.sort_order) {
        params.append("sort_by", payload?.sort_by);
        params.append("sort_order", payload?.sort_order);
      }
      const response = await axiosInstance.get(
        `${payoutsApis?.getAllPayouts}?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const listAllWithdrawalRequest = createAsyncThunk(
  "listAllWithdrawalRequest",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.get(
        payoutsApis.getAllWithdrawalRequest,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const listAllWalletTransction = createAsyncThunk(
  "listAllWalletTransction",
  async (payload = {}) => {
    try {
      const axiosInstance = createAxiosPayment();
      const params = new URLSearchParams();
      const sortBy = String(payload.sort_by ?? "CREATED_AT").toUpperCase();
      const sortOrder = payload.sort_order
        ? String(payload.sort_order).toUpperCase()
        : null;
      params.append("skip", payload.skip ?? 0);
      params.append("limit", payload.limit ?? 100);
      if (Array.isArray(payload.transaction_type)) {
        payload.transaction_type.forEach((type) => {
          params.append("transaction_type", type);
        });
      }
      if (payload.search) {
        params.append("search", payload.search);
      }
      if (sortOrder) {
        params.append("sort_by", sortBy);
        params.append("sort_order", sortOrder);
      }
      const response = await axiosInstance.get(
        `${payoutsApis.getAllWalletTransction}?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const listAllGiftCardRedemption = createAsyncThunk(
  "listAllGiftCardRedemption",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.get(
        payoutsApis.getAllGiftCardRedemption,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const updateGiftCardRedemptionDetails = createAsyncThunk(
  "updateGiftCardRedemptionDetails",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.post(
        payoutsApis.updateGiftCardRedemptionDetails,
        payload,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const downloadBarcodeRedemption = createAsyncThunk(
  "downloadRedemptionBarcode",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.post(
        `${payoutsApis.downloadRedemptionBarcode}?id=${payload?.id}`,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const uploadBarcodeRedemptionApi = createAsyncThunk(
  "uploadBarcodeRedemption",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.post(
        payoutsApis.uploadBarcodeRedemption,
        payload,
        {
          headers: {
            Authorization: `Bearer ${getUsers()?.access_token}`,
            "Content-Type": "multipart/form-data",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "DELETE, POST, GET, OPTIONS",
            "Access-Control-Allow-Headers":
              "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
            Accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const getPayoutInvoice = createAsyncThunk(
  "getPayoutInvoice",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.get(
        `${payoutsApis.downloadPayoutInvoice}?id=${payload?.payoutId}&document_type=${payload?.document_type}`,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const uploadPayoutReceipt = createAsyncThunk(
  "uploadPayoutReceipt",
  async ({ docFormData, payload }) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.post(
        `${payoutsApis.uploadPayout}?id=${payload?.id}&document_type=${payload?.document_type}`,
        docFormData,
        {
          headers: {
            Authorization: `Bearer ${getUsers()?.access_token}`,
            "Content-Type": "multipart/form-data",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "DELETE, POST, GET, OPTIONS",
            "Access-Control-Allow-Headers":
              "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
            Accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const uploadPayoutInvoice = createAsyncThunk(
  "uploadPayoutInvoice",
  async ({ formdata, uploadPayload }) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.post(
        `${payoutsApis.uploadPayout}?id=${uploadPayload?.id}&document_type=${uploadPayload?.document_type}&invoice_number=${uploadPayload?.invoice_number}`,
        formdata,
        {
          headers: {
            Authorization: `Bearer ${getUsers()?.access_token}`,
            "Content-Type": "multipart/form-data",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "DELETE, POST, GET, OPTIONS",
            "Access-Control-Allow-Headers":
              "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
            Accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const updatePayoutDetails = createAsyncThunk(
  "updatePayoutDetails",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.post(
        payoutsApis.updatePayoutDetails,
        payload,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const migratePayout = createAsyncThunk(
  "migratePayout",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.post(payoutsApis.migratePayout);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const updatePayoutCase = createAsyncThunk(
  "updatePayoutCase",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.post(
        payoutsApis.updateCase,
        payload,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const listAllPayoutsCase = createAsyncThunk(
  "listAllPayouts",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.get(payoutsApis.listAllCases);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const getByPayoutsCaseById = createAsyncThunk(
  "getByPayoutsCaseById",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.get(
        `${payoutsApis.getCaseByid}?shipment_id=${payload?.shipment_id}`,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const downloadPayoutCase = createAsyncThunk(
  "downloadPayoutCase",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.get(
        `${payoutsApis.downloadPayoutCase}?case_id=${payload?.id}`,
        { responseType: "blob" },
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

export const makePayPalPayment = createAsyncThunk(
  "makePayPalPaymentApis",
  async (payload) => {
    try {
      const axiosInstance = createAxiosPayment();
      const response = await axiosInstance.post(
        payoutsApis.paypalPaymentApi,
        payload,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      } else {
        throw new Error(error.response?.data?.detail || error.message);
      }
    }
  },
);

const payoutSlice = createSlice({
  name: "payoutSlice",
  initialState: {
    payoutSlice: {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(listAllPayouts.pending, (state) => {
        state.loading = true;
      })
      .addCase(listAllPayouts.fulfilled, (state, action) => {
        state.loading = false;
        state.quote = action.payload;
      })
      .addCase(listAllPayouts.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export default payoutSlice.reducer;
